import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
  type DragOverEvent,
  type DragMoveEvent,
} from '@dnd-kit/core';

import { Box } from '../../components/ui/box';
import { Text } from '../../components/ui/text';
import { useIsTouchDevice } from '../../hooks/use-is-touch-device.ts';
import { useIsMobile } from '../../hooks/use-media-query';
import { useMouseDrag } from '../../hooks/use-mouse-drag';
import { calculateSortOrder } from '../../lib/calculate-sort-order';
import { DND_OVERLAY_PROPS, DND_TOUCH_ACTIVATION } from '../../lib/dnd-attrs';
import { createVelocityTracker, settleOverlay } from '../../lib/drop-settle';
import { cn, cssEscape } from '../../lib/utils';

import { KanbanProvider, KanbanSelectionProvider } from './kanban-context';
import {
  COLUMN_WIDTH,
  COLLAPSED_HEIGHT,
  CARD_HEIGHT_BASE,
  MOBILE_BOARD_PADDING,
  columnOuterWidth,
  useWindowWidth,
} from './kanban-column';
import { KanbanColumnSkeleton } from './kanban-skeleton';
import { columnFirstCollision } from './kanban-collision';
import { shouldCommitDrop } from './kanban-drop';
import { cardDndId } from './kanban-ids';
import type {
  Id,
  KanbanBoardProps,
  KanbanContextValue,
  KanbanSelectionContextValue,
} from './types';

// Mount a column once it's within one column-width of the horizontal viewport,
// so it (and its dnd-kit droppable) registers before edge auto-scroll can carry
// a dragged card onto it.
const COLUMN_LAZY_OVERSCAN_PX = COLUMN_WIDTH;

/**
 * Defers mounting a column until it scrolls near the board's horizontal
 * viewport, so each column's data query + virtualizer only spin up on demand
 * instead of all N firing on load. Sticky: once revealed it stays mounted (the
 * same one-shot reveal as the data-table's `PlaceholderBody`). Until then it
 * reserves the column's exact outer width — mirroring the real column's
 * shrink-0 / self-start / snap-start so scroll geometry and mobile
 * scroll-snap stay stable when the real column swaps in.
 */
function LazyColumnSlot({
  rootRef,
  width,
  collapsed,
  isMobile,
  rootMarginPx,
  children,
}: {
  rootRef: React.RefObject<HTMLDivElement | null>;
  width: number;
  collapsed: boolean;
  isMobile: boolean;
  rootMarginPx: number;
  children: React.ReactNode;
}) {
  const placeholderRef = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (visible) return;
    const root = rootRef.current;
    const el = placeholderRef.current;
    // Require the board scroll container as the observer root; a null root would
    // silently fall back to the window viewport and mis-gate this horizontal
    // scroller. Both nodes are committed by the time this child effect runs.
    if (!root || !el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { root, rootMargin: `0px ${rootMarginPx}px` },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [visible, rootRef, rootMarginPx]);

  if (visible) return <>{children}</>;

  // Collapsed columns render as a fixed sunken rail (40×360); reserve the same.
  if (collapsed) {
    return (
      <Box alignSelf="start"
        ref={placeholderRef}
        width={width}
        height={COLLAPSED_HEIGHT}
        shrink={false}
        rounded="lg"
        // Stands in for a collapsed column mid-drag, so it has to sit at that
        // column's level or the board visibly dents where the placeholder is.
        surface="card"
        className={cn('', isMobile && 'snap-start')}
      />
    );
  }

  return (
    <Box alignSelf="start"
      ref={placeholderRef}
      width={width}
      minHeight={COLLAPSED_HEIGHT}
      shrink={false}
      className={cn('', isMobile && 'snap-start')}
    >
      <KanbanColumnSkeleton />
    </Box>
  );
}

// The render-slot argument shapes, named once. A `typeof props.x` query inside
// a callback below would read as a closure over `props` to anything that does
// not resolve types — the deps lint among them.
type ColumnHeaderArgs<TItem, TColumn> = Parameters<
  NonNullable<KanbanBoardProps<TItem, TColumn>["renderColumnHeader"]>
>[0];
type CollapsedColumnArgs<TItem, TColumn> = Parameters<
  NonNullable<KanbanBoardProps<TItem, TColumn>["renderCollapsedColumn"]>
>[0];

export function KanbanBoard<TItem, TColumn>(
  props: KanbanBoardProps<TItem, TColumn>,
) {
  const {
    columns,
    controls,
    emptyState,
    columnWidth,
    collapsedColumnIds: collapsedColumnIdsProp,
  } = props;
  const draggable = props.draggable ?? true;
  const isDropDisabled = props.isDropDisabled ?? false;

  const isMobile = useIsMobile();
  const isTouch = useIsTouchDevice();
  const windowWidth = useWindowWidth();
  const boardRef = useRef<HTMLDivElement | null>(null);

  // Click-and-drag panning of the horizontal board. Cards stay draggable:
  // they carry `data-dnd-draggable`, which the hook's press-drag guard skips,
  // so a press on a card goes to dnd-kit and only board chrome/gaps pan.
  useMouseDrag({ ref: boardRef });

  const [activeItem, setActiveItem] = useState<TItem | null>(null);
  const [activeColumnId, setActiveColumnId] = useState<Id | null>(null);
  const [overColumnId, setOverColumnId] = useState<Id | null>(null);
  const activeItemRef = useRef<TItem | null>(null);
  const activeColumnIdRef = useRef<Id | null>(null);
  // A drag that starts on a selected card carries the whole selection. The set
  // is snapshotted at drag start so a mid-flight selection change cannot
  // desync the group visuals from what the drop commits.
  const [dragGroupIds, setDragGroupIds] = useState<ReadonlySet<Id> | null>(
    null,
  );
  const dragGroupIdsRef = useRef<ReadonlySet<Id> | null>(null);

  // The drop settle (lib/drop-settle.ts): the overlay node is cloned at release
  // and flown onto the landed card with the pointer's release velocity. The
  // ref reads the overlay before the state change that unmounts it.
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const velocityRef = useRef(createVelocityTracker());

  // Board-owned (uncontrolled) collapse.
  const [internalCollapsed, setInternalCollapsed] = useState<Set<Id>>(
    () => new Set(),
  );
  const collapsedColumnIds = collapsedColumnIdsProp ?? internalCollapsed;
  // Ref-mirrored so handleDragEnd can open the column a drop landed on
  // without taking the live set as a dep.
  const collapsedColumnIdsRef = useRef(collapsedColumnIds);
  collapsedColumnIdsRef.current = collapsedColumnIds;

  // Consumer callbacks live in a ref synced every render so the context value
  // keeps a stable identity (its useMemo deps are ONLY drag state). Without
  // this, React.memo'd columns re-render on every drag tick.
  const cbRef = useRef(props);
  cbRef.current = props;

  const cancelSettleRef = useRef<() => void>(() => {});
  const settleTo = useCallback(
    (item: TItem | null, expectMove: boolean) => {
      const overlay = overlayRef.current;
      if (!overlay || item == null) return;
      cancelSettleRef.current();
      const id = cssEscape(String(cbRef.current.getItemId(item)));
      cancelSettleRef.current = settleOverlay({
        overlay,
        expectMove,
        velocity: velocityRef.current.velocity(),
        findTarget: () =>
          boardRef.current?.querySelector<HTMLElement>(`[data-kanban-item-id="${id}"]`) ?? null,
      });
    },
    [],
  );
  useEffect(() => () => cancelSettleRef.current(), []);

  const getItemId = useCallback(
    (i: TItem) => cbRef.current.getItemId(i),
    [],
  );
  const getSortValue = useCallback(
    (i: TItem) => cbRef.current.getSortValue(i),
    [],
  );
  const getColumnId = useCallback(
    (c: TColumn) => cbRef.current.getColumnId(c),
    [],
  );
  const getItemLabel = useCallback(
    (i: TItem) => cbRef.current.getItemLabel(i),
    [],
  );
  const getColumnLabel = useCallback(
    (c: TColumn) => cbRef.current.getColumnLabel(c),
    [],
  );
  const estimateItemSize = useCallback(
    (i: TItem | undefined, index: number) =>
      cbRef.current.estimateItemSize?.(i, index) ?? CARD_HEIGHT_BASE,
    [],
  );
  const renderCard = useCallback(
    (i: TItem, meta: { isDragOverlay: boolean; isSelected: boolean }) =>
      cbRef.current.renderCard(i, meta),
    [],
  );
  const onCardClick = useCallback(
    (i: TItem) => cbRef.current.onCardClick?.(i),
    [],
  );
  const isItemDragDisabled = useCallback(
    (i: TItem) => cbRef.current.isItemDragDisabled?.(i) ?? false,
    [],
  );
  const renderColumnHeader = useCallback(
    (a: ColumnHeaderArgs<TItem, TColumn>) =>
      cbRef.current.renderColumnHeader?.(a),
    [],
  );
  const renderColumnHeaderActions = useCallback(
    (c: TColumn) => cbRef.current.renderColumnHeaderActions?.(c),
    [],
  );
  const renderCollapsedColumn = useCallback(
    (a: CollapsedColumnArgs<TItem, TColumn>) =>
      cbRef.current.renderCollapsedColumn?.(a),
    [],
  );
  const renderColumnFooter = useCallback(
    (c: TColumn) => cbRef.current.renderColumnFooter?.(c),
    [],
  );
  const columnMenuItems = useCallback(
    (c: TColumn) => cbRef.current.columnMenuItems?.(c),
    [],
  );
  const onItemSelectedChange = useCallback(
    (i: TItem, selected: boolean) =>
      cbRef.current.onItemSelectedChange?.(i, selected),
    [],
  );
  // The card the user last toggled directly — the shift+click range pivot —
  // plus the ids the last shift+click range from it swept in (`rangeIds`),
  // which is what lets the next shift+click REPLACE that range Finder-style
  // instead of accumulating. A ref, not state: it only matters at the instant
  // of the next shift+click, so recording it must not re-render anything.
  const selectionAnchorRef = useRef<{
    columnId: Id;
    itemId: Id;
    rangeIds: ReadonlySet<Id> | null;
  } | null>(null);
  const setSelectionAnchor = useCallback((columnId: Id, itemId: Id) => {
    selectionAnchorRef.current = { columnId, itemId, rangeIds: null };
  }, []);
  const selectRangeTo = useCallback(
    (
      item: TItem,
      columnId: Id,
      orderedItems: readonly (TItem | undefined)[],
    ) => {
      const anchor = selectionAnchorRef.current;
      if (!anchor || anchor.columnId !== columnId) return false;
      const targetId = cbRef.current.getItemId(item);
      let anchorIndex = -1;
      let targetIndex = -1;
      for (let i = 0; i < orderedItems.length; i++) {
        const it = orderedItems[i];
        if (it == null) continue; // unloaded slot
        const id = cbRef.current.getItemId(it);
        if (id === anchor.itemId) anchorIndex = i;
        if (id === targetId) targetIndex = i;
      }
      if (anchorIndex === -1 || targetIndex === -1) return false;
      const lo = Math.min(anchorIndex, targetIndex);
      const hi = Math.max(anchorIndex, targetIndex);
      const selected = cbRef.current.selectedItemIds;
      const rangeIds = new Set<Id>();
      for (let i = 0; i < orderedItems.length; i++) {
        const it = orderedItems[i];
        if (it == null) continue;
        const id = cbRef.current.getItemId(it);
        if (i >= lo && i <= hi) {
          if (!(cbRef.current.isItemSelectable?.(it) ?? true)) continue;
          rangeIds.add(id);
          if (!selected?.has(id)) cbRef.current.onItemSelectedChange?.(it, true);
        } else if (anchor.rangeIds?.has(id) && selected?.has(id)) {
          // Finder semantics: a shift+click re-ranges from the same pivot, so
          // cards the PREVIOUS range swept in fall back out. Only the last
          // range's ids retract — cards selected directly (checkbox,
          // cmd/ctrl+click) are the user's own and stay.
          cbRef.current.onItemSelectedChange?.(it, false);
        }
      }
      // The pivot stays put; the recorded range is what the next shift+click
      // replaces. (Ids evicted from the loaded window can't be resolved back
      // to items and simply stay selected.)
      selectionAnchorRef.current = { ...anchor, rangeIds };
      return true;
    },
    [],
  );
  // A cleared selection (bulk-bar clear, filter reset) must not leave a stale
  // anchor behind — the next shift+click would silently sweep in a range the
  // user no longer sees as connected to anything.
  useEffect(() => {
    if (!props.selectedItemIds || props.selectedItemIds.size === 0) {
      selectionAnchorRef.current = null;
    }
  }, [props.selectedItemIds]);
  const isItemSelectable = useCallback(
    (i: TItem) => cbRef.current.isItemSelectable?.(i) ?? true,
    [],
  );
  const cardActions = useCallback(
    (i: TItem) => cbRef.current.cardActions?.(i),
    [],
  );
  const dragGroupLabel = useCallback(
    (count: number) => cbRef.current.dragGroupLabel?.(count) ?? `${count.toLocaleString()} items`,
    [],
  );

  // Selection context — re-published on every toggle (the set is the identity),
  // which is exactly the signal mounted cards repaint on. `null` keeps every
  // card's selection chrome unmounted — always on a touch device, where the
  // cluster could only appear on a held press and a finger's hold is the drag.
  const selectionCtxValue = React.useMemo<KanbanSelectionContextValue<TItem> | null>(
    () =>
      props.selectedItemIds && !isTouch
        ? {
            selectedItemIds: props.selectedItemIds,
            onItemSelectedChange,
            setSelectionAnchor,
            selectRangeTo,
            isItemSelectable,
            cardActions,
            dragGroupLabel,
          }
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- accessors are ref-stable; the set and the device drive re-publication
    [props.selectedItemIds, isTouch],
  );

  const onToggleCollapse = useCallback((id: Id) => {
    const controlled = cbRef.current.onToggleColumnCollapse;
    if (controlled) {
      controlled(id);
      return;
    }
    setInternalCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Column lookup map (ref-mirrored so drag handlers keep stable deps).
  const columnMap = React.useMemo(() => {
    const map = new Map<Id, TColumn>();
    for (const c of columns) map.set(getColumnId(c), c);
    return map;
  }, [columns, getColumnId]);
  const columnMapRef = useRef(columnMap);
  columnMapRef.current = columnMap;

  // Auto-scroll while dragging near board edges.
  const autoScrollRef = useRef<number | null>(null);

  const startAutoScroll = useCallback((direction: 'left' | 'right') => {
    if (autoScrollRef.current) return;
    const speed = direction === 'left' ? -8 : 8;
    autoScrollRef.current = window.setInterval(() => {
      boardRef.current?.scrollBy({ left: speed });
    }, 16);
  }, []);

  const stopAutoScroll = useCallback(() => {
    if (autoScrollRef.current) {
      clearInterval(autoScrollRef.current);
      autoScrollRef.current = null;
    }
  }, []);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const data = event.active.data.current;
    const item = data?.item as TItem | undefined;
    const colId = data?.columnId as Id | undefined;
    velocityRef.current.reset();
    if (item != null) {
      setActiveItem(item);
      activeItemRef.current = item;
      setActiveColumnId(colId ?? null);
      activeColumnIdRef.current = colId ?? null;
      // Grabbing a selected card lifts the whole selection; grabbing an
      // unselected card is always a single-card drag, selection untouched.
      const selected = cbRef.current.selectedItemIds;
      const group =
        selected && selected.size > 1 && selected.has(cbRef.current.getItemId(item))
          ? new Set(selected)
          : null;
      setDragGroupIds(group);
      dragGroupIdsRef.current = group;
    }
  }, []);

  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const { over } = event;
      if (!over) {
        setOverColumnId((prev) => (prev === null ? prev : null));
        stopAutoScroll();
        return;
      }
      const colId = (over.data.current?.columnId as Id | undefined) ?? null;
      setOverColumnId((prev) => (prev === colId ? prev : colId));
    },
    [stopAutoScroll],
  );

  const handleDragMove = useCallback(
    (event: DragMoveEvent) => {
      if (!boardRef.current) return;
      const el = boardRef.current;
      const rect = el.getBoundingClientRect();
      const edgeZone = 60;
      const activeRect = event.active.rect.current.translated;
      if (!activeRect) return;
      velocityRef.current.sample({ x: activeRect.left, y: activeRect.top });
      const centerX = activeRect.left + activeRect.width / 2;
      if (centerX < rect.left + edgeZone) startAutoScroll('left');
      else if (centerX > rect.right - edgeZone) startAutoScroll('right');
      else stopAutoScroll();
    },
    [startAutoScroll, stopAutoScroll],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const item = activeItemRef.current;
      const fromColumnId = activeColumnIdRef.current;
      const groupIds = dragGroupIdsRef.current;

      // Whether the release commits a move decides the settle's target: a
      // committed drop waits for the card to re-render in its new slot, a
      // rejected or same-slot release flies straight back onto the card.
      const { active: dropActive, over: dropOver } = event;
      const commits =
        item != null &&
        fromColumnId != null &&
        shouldCommitDrop({
          isDropDisabled: cbRef.current.isDropDisabled ?? false,
          hasOver: !!dropOver,
          hasItem: true,
          hasFromColumn: true,
        }) &&
        dropOver != null &&
        dropOver.id !== cardDndId(fromColumnId, cbRef.current.getItemId(item)) &&
        dropActive.id !== dropOver.id;
      settleTo(item, commits);

      stopAutoScroll();
      setActiveItem(null);
      activeItemRef.current = null;
      setActiveColumnId(null);
      activeColumnIdRef.current = null;
      setOverColumnId(null);
      setDragGroupIds(null);
      dragGroupIdsRef.current = null;

      const { active, over } = event;
      // Reject the drop when the grouping disables it or the release has no valid
      // target. shouldCommitDrop centralizes the rule (unit-tested in
      // kanban-drop); the nullish checks also narrow types for the math below.
      if (
        !shouldCommitDrop({
          isDropDisabled: cbRef.current.isDropDisabled ?? false,
          hasOver: !!over,
          hasItem: item != null,
          hasFromColumn: fromColumnId != null,
        }) ||
        !over ||
        item == null ||
        fromColumnId == null
      )
        return;
      const overData = over.data.current;
      if (!overData) return;
      const toColumnId = overData.columnId as Id;

      const { getItemId: getId, getSortValue: getSort } = cbRef.current;

      // Skip dropping on self in the same position. The card's dnd id is scoped
      // by its column (cardDndId), so releasing over the source card matches
      // only when it's the same card in the same column.
      if (over.id === cardDndId(fromColumnId, getId(item))) return;

      const overItem =
        overData.kind === 'item' ? (overData.item as TItem) : null;

      let position: number;
      let upperNeighborId: Id | undefined;
      let lowerNeighborId: Id | undefined;
      let upperSortValue: number | undefined;
      let lowerSortValue: number | undefined;
      let atUnloadedTail = false;

      if (overItem) {
        const overSort = getSort(overItem);
        // Place before/after the over card. Same-column compares sort values;
        // cross-column compares the active element's translated rect to it.
        let placeAfter: boolean;
        if (fromColumnId === toColumnId) {
          placeAfter = getSort(item) < overSort;
        } else {
          const overRect = over.rect;
          const activeRect = active.rect.current.translated;
          if (overRect && activeRect) {
            const overCenter = overRect.top + overRect.height / 2;
            const activeCenter = activeRect.top + activeRect.height / 2;
            placeAfter = activeCenter >= overCenter;
          } else {
            placeAfter = true;
          }
        }

        if (placeAfter) {
          upperNeighborId = getId(overItem);
          lowerNeighborId = overData.lowerNeighborId as Id | undefined;
          upperSortValue = overSort;
          lowerSortValue = overData.lowerSortValue as number | undefined;
        } else {
          lowerNeighborId = getId(overItem);
          upperNeighborId = overData.upperNeighborId as Id | undefined;
          upperSortValue = overData.upperSortValue as number | undefined;
          lowerSortValue = overSort;
        }
        position = calculateSortOrder(upperSortValue, lowerSortValue);
      } else {
        upperSortValue = overData.lastSortValue as number | undefined;
        const count = (overData.count as number | undefined) ?? 0;
        // A non-empty column whose last page has not landed (a collapsed
        // column never scrolls one in) has no tail to step past, so the drop is
        // an unanchored end-of-column append rather than a midpoint.
        atUnloadedTail = upperSortValue == null && count > 0;
        position = atUnloadedTail
          ? Number.MAX_SAFE_INTEGER
          : calculateSortOrder(upperSortValue, null);
      }

      const fromColumn = columnMapRef.current.get(fromColumnId);
      const toColumn = columnMapRef.current.get(toColumnId);
      if (fromColumn === undefined || toColumn === undefined) return;

      cbRef.current.onDragEnd?.({
        item,
        fromColumn,
        toColumn,
        position,
        upperNeighborId,
        lowerNeighborId,
        upperSortValue,
        lowerSortValue,
        atUnloadedTail,
        groupIds: groupIds ? Array.from(groupIds) : undefined,
      });

      // A collapsed column opens once the drop has landed in it — after, not
      // during, the drag, so the board never reflows under the pointer. A host
      // may take the drop-open itself, since a toggle can mean "keep open".
      if (collapsedColumnIdsRef.current.has(toColumnId)) {
        const dropOpen = cbRef.current.onDropIntoCollapsedColumn;
        if (dropOpen) dropOpen(toColumnId);
        else onToggleCollapse(toColumnId);
      }
    },
    [settleTo, stopAutoScroll, onToggleCollapse],
  );

  const handleDragCancel = useCallback(() => {
    settleTo(activeItemRef.current, false);
    stopAutoScroll();
    setActiveItem(null);
    activeItemRef.current = null;
    setActiveColumnId(null);
    activeColumnIdRef.current = null;
    setOverColumnId(null);
    setDragGroupIds(null);
    dragGroupIdsRef.current = null;
  }, [settleTo, stopAutoScroll]);

  // Capability-based sensors, never a viewport gate: a mouse drags after 8px
  // of travel, a finger after a hold (a swipe before the hold ends scrolls
  // the column). A phone-width board drags the same way a tablet does.
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: DND_TOUCH_ACTIVATION }),
  );

  // Context value — identity changes only on drag/collapse state plus the
  // explicit `renderVersion`, so a card-appearance change (touching no data
  // prop) still re-renders mounted cards through the ref-stable `renderCard`.
  const ctxValue = React.useMemo<KanbanContextValue<TItem, TColumn>>(
    () => ({
      activeItem,
      activeColumnId,
      overColumnId,
      dragGroupIds,
      getItemId,
      getItemLabel,
      getSortValue,
      getColumnId,
      getColumnLabel,
      estimateItemSize,
      renderCard,
      onCardClick,
      draggable,
      isItemDragDisabled,
      isDropDisabled,
      dropDisabledOverlay: props.dropDisabledOverlay,
      collapsedColumnIds,
      onToggleCollapse,
      renderColumnHeader,
      renderColumnHeaderActions,
      renderCollapsedColumn,
      renderColumnFooter,
      columnMenuItems,
      columnWidth,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- accessors are ref-stable; only drag/collapse state + the explicit renderVersion drive re-publication
    [
      activeItem,
      activeColumnId,
      overColumnId,
      dragGroupIds,
      draggable,
      isDropDisabled,
      props.dropDisabledOverlay,
      collapsedColumnIds,
      columnWidth,
      props.renderVersion,
    ],
  );

  // While a card is in flight the pointer belongs to the drag and to nothing
  // else. The ghost rides under the pointer, so whatever it is made of — a
  // labeled footer action, a hover-previewing chip, a token with a wash —
  // would otherwise rest under a still pointer and open after its delay,
  // and the cards the ghost passes over would wash and label as it slid by.
  // Every descendant of the board goes pointer-inert (dnd-kit tracks the
  // pointer on the document and finds drop targets by rect, so it needs no
  // hit-testing here), the overlay does the same for itself, and the board
  // root — now the hit target everywhere — wears the hand. The gesture the
  // press began is the only thing the pointer means until it ends.
  const dragging = activeItem != null;

  const boardContent = (
    <Box
      fill
      direction="column"
      data-dragging={dragging ? '' : undefined}
      cursor={dragging ? 'grabbing' : undefined}
      className="data-dragging:**:pointer-events-none"
    >
      {controls}
      {/* Horizontally scrollable board. Freeze native touch-scroll while a card
          is in flight so the browser doesn't pan the board underneath the
          pointer — dnd-kit's onDragMove drives edge auto-scroll instead. */}
      <Box overflowX="auto"
        ref={boardRef}
        // Names the board scroller for tooling that reads its geometry.
        data-kanban-board=""
        direction="row"
        gap="sm"
        paddingX={isMobile ? 'sm' : 'md'}
        paddingTop="sm"
        paddingBottom="md"
        flex={1}
        minHeight={0}
        className={cn(
          '',
          isMobile && !activeItem && 'snap-x snap-mandatory',
        )}
        style={{
          touchAction: activeItem ? 'none' : undefined,
          scrollPaddingLeft: isMobile ? MOBILE_BOARD_PADDING : undefined,
          scrollPaddingRight: isMobile ? MOBILE_BOARD_PADDING : undefined,
        }}
      >
        {columns.length === 0 ? (
          <Box fill align="center" justify="center">
            {emptyState ?? <Text variant="muted">No columns</Text>}
          </Box>
        ) : (
          columns.map((c, i) => {
            const id = getColumnId(c);
            const collapsed = collapsedColumnIds.has(id);
            return (
              <LazyColumnSlot
                key={id}
                rootRef={boardRef}
                width={columnOuterWidth({
                  collapsed,
                  columnWidth,
                  isMobile,
                  windowWidth,
                })}
                collapsed={collapsed}
                isMobile={isMobile}
                rootMarginPx={COLUMN_LAZY_OVERSCAN_PX}
              >
                {props.renderColumn(c, i)}
              </LazyColumnSlot>
            );
          })
        )}
      </Box>
    </Box>
  );

  if (!draggable) {
    return (
      <KanbanProvider value={ctxValue}>
        <KanbanSelectionProvider value={selectionCtxValue}>
          {boardContent}
        </KanbanSelectionProvider>
      </KanbanProvider>
    );
  }

  const activeIsSelected =
    activeItem != null &&
    (props.selectedItemIds?.has(getItemId(activeItem)) ?? false);

  return (
    <KanbanProvider value={ctxValue}>
      <KanbanSelectionProvider value={selectionCtxValue}>
        <DndContext
          sensors={sensors}
          collisionDetection={columnFirstCollision}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragMove={handleDragMove}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          {boardContent}

          <DragOverlay dropAnimation={null}>
            {activeItem != null ? (
              // The ghost takes no pointer: it is a picture of the card, and
              // the board root under it is what the hit lands on — which is
              // where the drag's `grabbing` cursor lives.
              <Box
                ref={overlayRef}
                width={columnWidth ?? COLUMN_WIDTH}
                pointerEvents="none"
                {...DND_OVERLAY_PROPS}
              >
                {dragGroupIds ? (
                  <KanbanDragStack count={dragGroupIds.size}>
                    {renderCard(activeItem, {
                      isDragOverlay: true,
                      isSelected: true,
                    })}
                  </KanbanDragStack>
                ) : (
                  renderCard(activeItem, {
                    isDragOverlay: true,
                    isSelected: activeIsSelected,
                  })
                )}
              </Box>
            ) : null}
          </DragOverlay>
        </DndContext>
      </KanbanSelectionProvider>
    </KanbanProvider>
  );
}

/**
 * The group-drag overlay: the active card riding on top of two card-surface
 * silhouettes, with a count badge. The silhouettes mount flush behind the card
 * and transition to their offsets one frame later, so picking up a selection
 * reads as the cards gathering into a stack.
 */
function KanbanDragStack({
  count,
  children,
}: {
  count: number;
  children: React.ReactNode;
}) {
  const [fanned, setFanned] = useState(false);
  useEffect(() => {
    const raf = requestAnimationFrame(() => setFanned(true));
    return () => cancelAnimationFrame(raf);
  }, []);
  const silhouette =
    'inset-0 border transition-transform duration-base ease-out';
  return (
    <Box position="relative">
      {/* Rendered before the card; among positioned siblings DOM order paints
          later-on-top, so these sit behind it and the badge sits above. */}
      <Box
        aria-hidden
        position="absolute"
        surface="card"
        rounded="md"
        className={cn(silhouette, fanned && 'translate-x-2 translate-y-2 rotate-1')}
      />
      <Box
        aria-hidden
        position="absolute"
        surface="card"
        rounded="md"
        className={cn(silhouette, fanned && 'translate-x-1 translate-y-1 rotate-[0.5deg]')}
      />
      <Box position="relative">{children}</Box>
      <Box
        position="absolute"
        align="center"
        justify="center"
        className="-top-2 -right-2 size-6 rounded-full bg-brand"
      >
        <Text size="xs" weight="semibold" className="text-white">
          {count.toLocaleString()}
        </Text>
      </Box>
    </Box>
  );
}
